import { useMemo } from "react";
import { usePermissions } from "@/features/auth/hooks/usePermission";
import {
  isGranted,
  type Permission,
  type Permissions,
} from "@/features/auth/models/permissions";
import { useAuthStore } from "@/features/auth/stores/authStore";
import type { CasinoCategory } from "@/features/casino/models/casinoCategory";
import { useAvailableCasinoCategories } from "@/features/casino/hooks/useAvailableCasinoCategories";
import {
  useLiveEntriesCount,
  useUpcomingEntriesCount,
} from "@/hooks/firestore/betting/useEntries";
import {
  type EventCounts,
  useEventCounts,
} from "@/hooks/firestore/betting/useEventCounts";
import { usePickemsContests } from "@/hooks/firestore/pickems/usePickemsContests";
import {
  type DefaultHubPreferences,
  useHomeUrl,
  usePreferences,
} from "@/hooks/firestore/user/usePreferences";
import useHubs from "@/hooks/graphql/useHubs";
import { type Titles, useTitles } from "@/hooks/graphql/useTitles";
import useLocale from "@/hooks/useLocale";
import useMustHidePickems from "@/hooks/useMustHidePickems";
import { account } from "../navItems/account";
import { bettingHub, live } from "../navItems/betting";
import { casino } from "../navItems/casino";
import { pickems } from "../navItems/pickems";
import { helpdesk, logo, promotions, terms } from "../navItems/static";
import { preferredFirst } from "../utils/preferredFirst";
import type { NavHub, NavItem } from "../types";

export type NavData = {
  hubs: NavHub[];
  titles: Titles;
  eventCounts: EventCounts | undefined;
  permissions: Permissions;
  defaultHub: DefaultHubPreferences | null;
  contestsCounts: Record<string, number>;
  liveEntriesCount: number;
  upcomingEntriesCount: number;
  locale: string | null;
  hidePickems: boolean;
  homeUrl: string;
  casinoCategories: CasinoCategory[];
};

const canViewCasino = ({ viewCasino }: Permissions) =>
  Boolean(viewCasino) && viewCasino !== "BLOCKED";

export const useNavData = (): { data: NavData; isSettled: boolean } => {
  const hubs = useHubs();
  const titles = useTitles();
  const permissions = usePermissions();
  const locale = useLocale();
  const hidePickems = useMustHidePickems();
  const liveEntriesCount = useLiveEntriesCount();
  const upcomingEntriesCount = useUpcomingEntriesCount();
  const isAuthLoading = useAuthStore((state) => state.status === "loading");
  const { preferences, isLoading: isLoadingPreferences } = usePreferences();
  const { eventCounts, isLoading: isLoadingEventCounts } = useEventCounts();
  const { contestsCounts, isLoading: isLoadingContests } = usePickemsContests();
  const { homeUrl } = useHomeUrl();
  // mounted on every page, so load once rather than polling
  const { availableCategories: casinoCategories } =
    useAvailableCasinoCategories({
      enabled: canViewCasino(permissions),
      refetchInterval: false,
    });

  const navHubs = useMemo<NavHub[]>(
    () =>
      hubs.flatMap(({ slug, name, pathPrefix, titles }) =>
        slug && name ? { slug, name, pathPrefix, titles } : [],
      ),
    [hubs],
  );

  const data = useMemo<NavData>(
    () => ({
      hubs: navHubs,
      titles,
      eventCounts,
      permissions,
      defaultHub: preferences.defaultHub,
      contestsCounts,
      liveEntriesCount,
      upcomingEntriesCount,
      locale,
      hidePickems,
      homeUrl,
      casinoCategories,
    }),
    [
      navHubs,
      titles,
      eventCounts,
      permissions,
      preferences.defaultHub,
      contestsCounts,
      liveEntriesCount,
      upcomingEntriesCount,
      locale,
      hidePickems,
      homeUrl,
      casinoCategories,
    ],
  );

  return {
    data,
    isSettled:
      !isAuthLoading &&
      !isLoadingPreferences &&
      !isLoadingEventCounts &&
      !isLoadingContests,
  };
};

const isNavItem = (item: NavItem | false | undefined): item is NavItem =>
  Boolean(item);

type HubOptions = {
  showsBettingHubs: boolean;
  showsLive: boolean;
};

// The rail and the Bottom nav both take their hubs from here, so they agree
// on the order.
export const orderedHubItems = (
  {
    hubs,
    eventCounts,
    permissions,
    defaultHub,
    locale,
    casinoCategories,
  }: NavData,
  { showsBettingHubs, showsLive }: HubOptions,
): NavItem[] => {
  const showsHub = (...needed: Permission[]) =>
    showsBettingHubs &&
    needed.every((permission) => isGranted(permissions[permission]));

  const hub = (slug: string) =>
    hubs.find((candidate) => candidate.slug === slug);

  const esports = hub("esports");
  const sports = hub("sports");
  const racing = hub("racing");

  const hubItems = [
    sports &&
      showsHub("viewBettingMarkets", "viewSportsMarkets") &&
      bettingHub(sports, eventCounts, locale),
    sports &&
      showsLive &&
      showsHub("viewBettingMarkets", "viewSportsMarkets", "viewLiveMarkets") &&
      live(sports, eventCounts),
    racing &&
      showsHub("viewBettingMarkets", "viewRacingMarkets") &&
      bettingHub(racing, eventCounts, locale),
    esports &&
      showsHub("viewBettingMarkets", "viewEsportsMarkets") &&
      bettingHub(
        esports,
        eventCounts,
        locale,
        showsLive && isGranted(permissions.viewLiveMarkets)
          ? live(esports, eventCounts, `Live ${esports.name}`)
          : undefined,
      ),
    canViewCasino(permissions) && casino(casinoCategories),
  ].filter(isNavItem);

  return preferredFirst(hubItems, defaultHub);
};

const buildNavTree = (data: NavData): NavItem[] => {
  const {
    titles,
    permissions,
    contestsCounts,
    liveEntriesCount,
    upcomingEntriesCount,
    hidePickems,
    homeUrl,
  } = data;

  return [
    logo(homeUrl),
    isGranted(permissions.user) &&
      account(liveEntriesCount + upcomingEntriesCount),
    ...orderedHubItems(data, { showsBettingHubs: true, showsLive: true }),
    !hidePickems && pickems(titles, contestsCounts),
    promotions,
    terms,
    helpdesk,
  ].filter(isNavItem);
};

export const useNavTree = (): {
  items: NavItem[];
  isSettled: boolean;
  skeletonRows: number;
} => {
  const { data, isSettled } = useNavData();
  const items = useMemo(() => buildNavTree(data), [data]);

  return { items, isSettled, skeletonRows: 2 + data.hubs.length + 3 };
};
